import React, { useState, useEffect } from 'react';
import { Button } from './ui/button.jsx';
import { Badge } from './ui/badge.jsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select.jsx';
import {
  Loader2,
  Download,
  Music,
  Mic2,
  Drum,
  Piano,
  Guitar,
  Waves,
  Scissors,
  CheckCircle,
  Wind,
  Music2,
  FileMusic,
} from 'lucide-react';
import api from '../services/api';
import StemProgressModal from './StemProgressModal';
import MidiConversionModal from './MidiConversionModal';
import CreditConfirmModal from './CreditConfirmModal';

const STEM_CONFIG = {
  drum: { name: 'Drum', icon: Drum, color: 'bg-orange-500/20 text-orange-500' },
  bass: { name: 'Bass', icon: Waves, color: 'bg-blue-500/20 text-blue-500' },
  electric_guitar: { name: 'Electric Guitar', icon: Guitar, color: 'bg-red-500/20 text-red-500' },
  acoustic_guitar: { name: 'Acoustic Guitar', icon: Guitar, color: 'bg-amber-500/20 text-amber-500' },
  piano: { name: 'Piano', icon: Piano, color: 'bg-green-500/20 text-green-500' },
  synthesizer: { name: 'Synthesizer', icon: Waves, color: 'bg-cyan-500/20 text-cyan-500' },
  strings: { name: 'Strings', icon: Music2, color: 'bg-violet-500/20 text-violet-500' },
  wind: { name: 'Wind', icon: Wind, color: 'bg-teal-500/20 text-teal-500' },
};

const STEM_ORDER = ['drum', 'bass', 'electric_guitar', 'acoustic_guitar', 'piano', 'synthesizer', 'strings', 'wind'];

function AvailableTrackCard({ trackType, trackData, label, sessionId, midiConversion, onMidiClick }) {
  const [downloading, setDownloading] = useState(null);
  
  const getTrackConfig = () => {
    if (trackType === 'original_vocal') {
      return { name: 'Original Vocal', icon: Mic2, color: 'bg-purple-500/20 text-purple-500' };
    }
    if (trackType === 'clean_vocal') {
      return { name: 'Clean Vocal', icon: Mic2, color: 'bg-emerald-500/20 text-emerald-500' };
    }
    if (trackType === 'instrumental') {
      return { name: 'Instrumental', icon: Music, color: 'bg-indigo-500/20 text-indigo-500' };
    }
    return { name: label || trackType, icon: Music, color: 'bg-gray-500/20 text-gray-500' };
  };
  
  const config = getTrackConfig();
  const Icon = config.icon;

  const handleDownload = async (url, suffix) => {
    if (!url) return;
    setDownloading(suffix);
    
    try {
      const response = await api.get(`/download-url?s3_url=${encodeURIComponent(url)}`);
      const presignedUrl = response.data.download_url;
      
      const link = document.createElement('a');
      link.href = presignedUrl;
      link.download = `${config.name}${suffix}.mp3`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error getting download URL:', error);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${config.name}${suffix}.mp3`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadMidi = async () => {
    if (!midiConversion?.midi_url) return;
    setDownloading('midi');
    
    try {
      const midiUrl = midiConversion.midi_url;
      const cleanUrl = midiUrl.includes('/api/proxy/audio?url=') 
        ? decodeURIComponent(midiUrl.split('/api/proxy/audio?url=')[1])
        : midiUrl;
      
      const response = await api.get(`/download-url?s3_url=${encodeURIComponent(cleanUrl)}`);
      const presignedUrl = response.data.download_url;
      
      const link = document.createElement('a');
      link.href = presignedUrl;
      link.download = `${config.name}.mid`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading MIDI:', error);
      window.open(midiConversion.midi_url, '_blank');
    } finally {
      setDownloading(null);
    }
  };

  const hasMidi = midiConversion?.status === 'COMPLETED' && midiConversion?.midi_url;
  const midiInProgress = midiConversion?.status === 'IN_QUEUE' || midiConversion?.status === 'PROCESSING';

  return (
    <div className="border rounded-lg p-4 bg-card hover:border-primary/50 transition-colors">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${config.color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h4 className="font-medium">{config.name}</h4>
          <p className="text-xs text-muted-foreground">Available track</p>
        </div>
        <Badge variant="secondary" className="ml-auto text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
          <CheckCircle className="h-3 w-3 mr-1" />
          Ready
        </Badge>
      </div>
      
      <div className="space-y-2">
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full justify-start"
          onClick={() => handleDownload(trackData, '')}
          disabled={downloading !== null}
        >
          {downloading === '' ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Download
        </Button>
        
        {hasMidi ? (
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full justify-start text-muted-foreground"
            onClick={handleDownloadMidi}
            disabled={downloading === 'midi'}
          >
            {downloading === 'midi' ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileMusic className="h-4 w-4 mr-2" />
            )}
            Download MIDI
          </Button>
        ) : (
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full justify-start text-muted-foreground"
            onClick={() => onMidiClick(trackType, trackData, config.name)}
            disabled={midiInProgress}
          >
            {midiInProgress ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileMusic className="h-4 w-4 mr-2" />
            )}
            {midiInProgress ? 'Converting...' : 'Audio to MIDI'}
          </Button>
        )}
      </div>
    </div>
  );
}

function StemCard({ stemType, stemData, config, sessionId, midiConversion, onMidiClick, onClearMidi }) {
  const Icon = config.icon || Music;
  const [downloading, setDownloading] = React.useState(null);
  const [clearing, setClearing] = React.useState(false);
  
  const handleDownload = async (url, suffix) => {
    if (!url) return;
    
    const downloadType = suffix || 'stem';
    setDownloading(downloadType);
    
    try {
      const response = await api.get(`/download-url?s3_url=${encodeURIComponent(url)}`);
      const presignedUrl = response.data.download_url;
      
      const link = document.createElement('a');
      link.href = presignedUrl;
      link.download = `${config.name}${suffix}.mp3`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error getting download URL:', error);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${config.name}${suffix}.mp3`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadMidi = async () => {
    if (!midiConversion?.midi_url) return;
    setDownloading('midi');
    
    try {
      const midiUrl = midiConversion.midi_url;
      const cleanUrl = midiUrl.includes('/api/proxy/audio?url=') 
        ? decodeURIComponent(midiUrl.split('/api/proxy/audio?url=')[1])
        : midiUrl;
      
      const response = await api.get(`/download-url?s3_url=${encodeURIComponent(cleanUrl)}`);
      const presignedUrl = response.data.download_url;
      
      const link = document.createElement('a');
      link.href = presignedUrl;
      link.download = `${config.name}.mid`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading MIDI:', error);
      window.open(midiConversion.midi_url, '_blank');
    } finally {
      setDownloading(null);
    }
  };

  const hasMidi = midiConversion?.status === 'COMPLETED' && midiConversion?.midi_url;
  const midiInProgress = midiConversion?.status === 'IN_QUEUE' || midiConversion?.status === 'PROCESSING';

  return (
    <div className="border rounded-lg p-4 bg-card hover:border-primary/50 transition-colors">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${config.color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h4 className="font-medium">{config.name}</h4>
          <p className="text-xs text-muted-foreground">Separated stem</p>
        </div>
        <Badge variant="secondary" className="ml-auto text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
          <CheckCircle className="h-3 w-3 mr-1" />
          Ready
        </Badge>
      </div>
      
      <div className="space-y-2">
        {stemData?.stem_url && (
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full justify-start"
            onClick={() => handleDownload(stemData.stem_url, '')}
            disabled={downloading !== null}
          >
            {downloading === 'stem' ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Download {config.name}
          </Button>
        )}
        {stemData?.back_url && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full justify-start text-muted-foreground"
            onClick={() => handleDownload(stemData.back_url, ' (No ' + config.name + ')')}
            disabled={downloading !== null}
          >
            {downloading?.includes('No') ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Without {config.name}
          </Button>
        )}
        
        {stemData?.stem_url && (
          hasMidi ? (
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full justify-start text-muted-foreground"
              onClick={handleDownloadMidi}
              disabled={downloading === 'midi'}
            >
              {downloading === 'midi' ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileMusic className="h-4 w-4 mr-2" />
              )}
              Download MIDI
            </Button>
          ) : midiInProgress ? (
            <div className="flex gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                className="flex-1 justify-start text-muted-foreground"
                disabled={true}
              >
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Converting...
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-orange-500 hover:text-orange-600 hover:bg-orange-100/50 px-2"
                onClick={async () => {
                  setClearing(true);
                  try {
                    await onClearMidi(stemType);
                  } finally {
                    setClearing(false);
                  }
                }}
                disabled={clearing}
                title="Clear stuck conversion and retry"
              >
                {clearing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <span className="text-xs">Reset</span>
                )}
              </Button>
            </div>
          ) : (
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full justify-start text-muted-foreground"
              onClick={() => onMidiClick(stemType, stemData.stem_url, config.name)}
            >
              <FileMusic className="h-4 w-4 mr-2" />
              Audio to MIDI
            </Button>
          )
        )}
      </div>
    </div>
  );
}

export default function StemSeparation({ sessionId, existingStems, sessionFiles, embedded = false, userMinutes = 0, onCreditsUsed }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [stems, setStems] = useState(existingStems || {});
  const [initialized, setInitialized] = useState(false);
  const [isInProgress, setIsInProgress] = useState(false);
  const [selectedStemType, setSelectedStemType] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [currentProcessingStem, setCurrentProcessingStem] = useState(null);
  const [liveMinutes, setLiveMinutes] = useState(userMinutes);
  
  const [midiConversions, setMidiConversions] = useState({});
  const [midiModalOpen, setMidiModalOpen] = useState(false);
  const [midiTrackType, setMidiTrackType] = useState(null);
  const [midiTrackName, setMidiTrackName] = useState('');
  const [midiAudioUrl, setMidiAudioUrl] = useState(null);

  useEffect(() => {
    const fetchInitialStatus = async () => {
      try {
        const response = await api.get(`/sessions/${sessionId}/stems-status`);
        const data = response.data;
        
        if (data.stems && Object.keys(data.stems).length > 0) {
          setStems(data.stems);
        }
        
        if (data.stems_progress) {
          if (data.stems_progress.phase !== 'idle' && 
              data.stems_progress.phase !== 'complete' &&
              data.stems_progress.phase !== 'error') {
            setIsInProgress(true);
            setCurrentProcessingStem(data.stems_progress.current_stem);
            setIsModalOpen(true);
          }
        }
      } catch (err) {
        console.error('Failed to fetch initial stems status:', err);
      } finally {
        setInitialized(true);
      }
    };
    
    if (!existingStems || Object.keys(existingStems).length === 0) {
      fetchInitialStatus();
    } else {
      setInitialized(true);
    }
  }, [sessionId, existingStems]);

  useEffect(() => {
    const fetchMidiConversions = async () => {
      try {
        const response = await api.get(`/sessions/${sessionId}/midi-conversions`);
        if (response.data.success && response.data.conversions) {
          setMidiConversions(response.data.conversions);
        }
      } catch (err) {
        console.error('Failed to fetch MIDI conversions:', err);
      }
    };
    
    fetchMidiConversions();
  }, [sessionId]);

  const handleMidiClick = (trackType, audioUrl, trackName) => {
    setMidiTrackType(trackType);
    setMidiAudioUrl(audioUrl);
    setMidiTrackName(trackName);
    setMidiModalOpen(true);
  };

  const handleMidiComplete = (trackType, midiUrl) => {
    setMidiConversions(prev => ({
      ...prev,
      [trackType]: {
        ...prev[trackType],
        status: 'COMPLETED',
        midi_url: midiUrl
      }
    }));
  };

  const handleClearMidi = async (trackType) => {
    try {
      await api.delete(`/sessions/${sessionId}/midi-conversion/${trackType}`);
      setMidiConversions(prev => {
        const updated = { ...prev };
        delete updated[trackType];
        return updated;
      });
    } catch (err) {
      console.error('Failed to clear MIDI conversion:', err);
    }
  };

  const handleMidiModalClose = async () => {
    setMidiModalOpen(false);
    setMidiTrackType(null);
    setMidiAudioUrl(null);
    setMidiTrackName('');
    
    // Refresh MIDI conversions to get latest status
    try {
      const response = await api.get(`/sessions/${sessionId}/midi-conversions`);
      if (response.data.success && response.data.conversions) {
        setMidiConversions(response.data.conversions);
      }
    } catch (err) {
      console.error('Failed to refresh MIDI conversions:', err);
    }
  };

  const handleStemSeparationClick = async () => {
    if (!selectedStemType) return;
    try {
      const response = await api.get('/user/profile');
      setLiveMinutes(response.data.minutes || 0);
    } catch (err) {
      console.error('Failed to fetch user minutes:', err);
    }
    setShowConfirmModal(true);
  };

  const startSingleStemSeparation = async () => {
    if (!selectedStemType) return;
    
    setShowConfirmModal(false);
    setCurrentProcessingStem(selectedStemType);
    setIsModalOpen(true);
    setIsInProgress(true);
    
    try {
      await api.post(`/sessions/${sessionId}/separate-stem`, {
        stem_type: selectedStemType
      });
      if (onCreditsUsed) onCreditsUsed();
    } catch (err) {
      console.error('Failed to start stem separation:', err);
      setIsInProgress(false);
      setCurrentProcessingStem(null);
    }
  };

  const handleSeparationComplete = (newStems) => {
    setStems(prev => ({ ...prev, ...newStems }));
    setIsInProgress(false);
    setCurrentProcessingStem(null);
    setSelectedStemType('');
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  if (!initialized) {
    return (
      <div className="border rounded-lg p-6 bg-muted/30">
        <div className="flex items-center justify-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <span className="font-medium">Loading Stems...</span>
        </div>
      </div>
    );
  }

  const availableTracks = [];
  if (sessionFiles?.vocal_url) {
    availableTracks.push({ type: 'original_vocal', url: sessionFiles.vocal_url });
  }
  if (sessionFiles?.clean_vocal_url) {
    availableTracks.push({ type: 'clean_vocal', url: sessionFiles.clean_vocal_url });
  }
  if (sessionFiles?.instrumental_url) {
    availableTracks.push({ type: 'instrumental', url: sessionFiles.instrumental_url });
  }

  const extractedStems = Object.keys(stems);
  const availableStemTypes = STEM_ORDER.filter(st => !extractedStems.includes(st));

  const content = (
    <>
      {availableTracks.length > 0 && (
          <div className="mb-6">
            <div className="text-xs text-muted-foreground font-medium mb-3 flex items-center gap-2">
              <Mic2 className="h-3.5 w-3.5" />
              Available Tracks
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {availableTracks.map((track) => (
                <AvailableTrackCard
                  key={track.type}
                  trackType={track.type}
                  trackData={track.url}
                  sessionId={sessionId}
                  midiConversion={midiConversions[track.type]}
                  onMidiClick={handleMidiClick}
                />
              ))}
            </div>
          </div>
        )}

        {extractedStems.length > 0 && (
          <div className="mb-6">
            <div className="text-xs text-muted-foreground font-medium mb-3 flex items-center gap-2">
              <Music className="h-3.5 w-3.5" />
              Extracted Stems
              <Badge variant="secondary" className="text-xs ml-1">
                {extractedStems.length} tracks
              </Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {STEM_ORDER.filter(st => extractedStems.includes(st)).map((stemType) => {
                const config = STEM_CONFIG[stemType] || { 
                  name: stemType, 
                  icon: Music, 
                  color: 'bg-gray-500/20 text-gray-500' 
                };
                return (
                  <StemCard
                    key={stemType}
                    stemType={stemType}
                    stemData={stems[stemType]}
                    config={config}
                    sessionId={sessionId}
                    midiConversion={midiConversions[stemType]}
                    onMidiClick={handleMidiClick}
                    onClearMidi={handleClearMidi}
                  />
                );
              })}
            </div>
          </div>
        )}

        {availableStemTypes.length > 0 && (
          <div className="border-t pt-4">
            <div className="text-xs text-muted-foreground font-medium mb-3 flex items-center gap-2">
              <Scissors className="h-3.5 w-3.5" />
              Extract New Stem
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Select 
                value={selectedStemType} 
                onValueChange={setSelectedStemType}
                disabled={isInProgress}
              >
                <SelectTrigger className="w-full sm:w-[280px]">
                  <SelectValue placeholder="Select stem type to extract..." />
                </SelectTrigger>
                <SelectContent>
                  {availableStemTypes.map((stemType) => {
                    const config = STEM_CONFIG[stemType];
                    const Icon = config?.icon || Music;
                    return (
                      <SelectItem key={stemType} value={stemType}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          <span>{config?.name || stemType}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <Button 
                onClick={handleStemSeparationClick} 
                disabled={!selectedStemType || isInProgress}
                className="sm:w-auto"
              >
                {isInProgress ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Scissors className="h-4 w-4 mr-2" />
                    Process Stem
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Each stem is processed individually using AI-powered separation.
            </p>
          </div>
        )}

        {availableStemTypes.length === 0 && extractedStems.length === STEM_ORDER.length && (
          <div className="border-t pt-4 text-center text-sm text-muted-foreground">
            <CheckCircle className="h-5 w-5 text-green-500 mx-auto mb-2" />
            All available stems have been extracted!
          </div>
        )}
    </>
  );

  return (
    <>
      {embedded ? (
        content
      ) : (
        <div className="border rounded-lg p-4 bg-card">
          <div className="flex items-center gap-2 mb-4">
            <Scissors className="h-5 w-5 text-primary" />
            <h4 className="font-semibold">Stems & Tracks</h4>
          </div>
          {content}
        </div>
      )}
      
      <StemProgressModal
        isOpen={isModalOpen}
        sessionId={sessionId}
        selectedStems={currentProcessingStem ? [currentProcessingStem] : []}
        onClose={handleModalClose}
        onComplete={handleSeparationComplete}
      />
      
      <MidiConversionModal
        isOpen={midiModalOpen}
        onClose={handleMidiModalClose}
        sessionId={sessionId}
        trackType={midiTrackType}
        trackName={midiTrackName}
        audioUrl={midiAudioUrl}
        onComplete={handleMidiComplete}
      />

      <CreditConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={startSingleStemSeparation}
        operationName="Stem Separation"
        operationCost={0.5}
        currentCredits={liveMinutes}
        icon={Scissors}
      />
    </>
  );
}
